import fixWebmDuration from "fix-webm-duration";

/** Chrome MediaRecorder WebM often lacks Duration — seek bar jumps to end. */
export async function probeWebmDurationMs(blob: Blob): Promise<number | null> {
  const url = URL.createObjectURL(blob);
  try {
    const video = document.createElement("video");
    video.preload = "auto";
    video.muted = true;

    await new Promise<void>((resolve, reject) => {
      const onError = () => reject(new Error("video metadata failed"));
      video.onloadedmetadata = () => resolve();
      video.onerror = onError;
      video.src = url;
    });

    const metaDuration = video.duration;
    if (
      Number.isFinite(metaDuration) &&
      metaDuration > 0 &&
      metaDuration !== Infinity
    ) {
      return metaDuration * 1000;
    }

    await new Promise<void>(resolve => {
      video.onseeked = () => resolve();
      video.currentTime = 1e10;
    });

    const seekDuration = video.currentTime;
    if (Number.isFinite(seekDuration) && seekDuration > 0) {
      return seekDuration * 1000;
    }
    return null;
  } catch {
    return null;
  } finally {
    URL.revokeObjectURL(url);
  }
}

export function isProbablyWebmBlob(blob: Blob, filenameHint?: string): boolean {
  const type = (blob.type ?? "").toLowerCase();
  if (type.includes("webm")) {
    return true;
  }
  return Boolean(filenameHint && /\.webm$/i.test(filenameHint));
}

/**
 * Patch WebM duration metadata so players show correct length and seek bar.
 * @param durationMs Known recording length; probed from bytes when omitted.
 */
export async function repairWebmBlob(
  blob: Blob,
  durationMs?: number,
  filenameHint?: string
): Promise<Blob> {
  if (!isProbablyWebmBlob(blob, filenameHint)) {
    return blob;
  }

  let ms = durationMs;
  if (ms == null || !Number.isFinite(ms) || ms <= 0) {
    ms = (await probeWebmDurationMs(blob)) ?? undefined;
  }
  if (ms == null || ms <= 0) {
    return blob;
  }

  try {
    return await fixWebmDuration(blob, ms, { logger: false });
  } catch {
    return blob;
  }
}
